import { useState } from 'react';
import type { DashboardData, DashboardLayoutItem, DashboardWidgetWidth } from '@/types';
import { Icon } from '@/components/Icon';
import { useAuth } from '@/auth/AuthContext';
import { WIDGET_MAP, widgetAllowedForPerms } from './registry';

interface Props {
  data: DashboardData;
  editing: boolean;
  layout: DashboardLayoutItem[];
  setLayout: (l: DashboardLayoutItem[]) => void;
}

function spanClass(w: DashboardWidgetWidth): string {
  return w === 'kpi' ? 'md:col-span-3' : w === 'half' ? 'md:col-span-6' : 'md:col-span-12';
}

const WIDTH_LABEL: Record<DashboardWidgetWidth, string> = { kpi: '¼', half: '½', full: '1/1' };

/**
 * Renderuje widoczne widgety w responsywnej siatce 12-kolumnowej (col-span wg szerokości).
 * W trybie edycji dokłada „chrome" (przeciąganie natywnym HTML5 DnD, zmiana szerokości, ukrycie).
 * Gating ról stosowany przy renderze — widgety niedozwolone dla roli w ogóle się nie pojawiają.
 */
export function DashboardGrid({ data, editing, layout, setLayout }: Props) {
  const { user } = useAuth();
  // Gating widgetów idzie po UPRAWNIENIACH (macierz), nie po nazwach ról —
  // role są dynamiczne i tworzone w UI.
  const perms = user?.permissions;

  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const visible = layout.filter(it => it.visible && widgetAllowedForPerms(it.key, perms));

  const reorder = (from: string | null, to: string) => {
    if (!from || from === to) return;
    const arr = [...layout];
    const fi = arr.findIndex(x => x.key === from);
    const ti = arr.findIndex(x => x.key === to);
    if (fi < 0 || ti < 0) return;
    const [moved] = arr.splice(fi, 1);
    arr.splice(ti, 0, moved);
    setLayout(arr);
  };

  /**
   * Przesunięcie o jedno miejsce wśród WIDOCZNYCH kafelków — dla dotyku i klawiatury,
   * gdzie przeciąganie HTML5 nie działa. Ukryte zostają na swoich miejscach w układzie.
   */
  const step = (key: string, dir: -1 | 1) => {
    const vi = visible.findIndex(x => x.key === key);
    const neighbour = visible[vi + dir];
    if (!neighbour) return;
    const arr = layout.filter(x => x.key !== key);
    const ni = arr.findIndex(x => x.key === neighbour.key);
    arr.splice(dir < 0 ? ni : ni + 1, 0, layout.find(x => x.key === key)!);
    setLayout(arr);
  };

  const cycleWidth = (key: string) => {
    const def = WIDGET_MAP[key];
    setLayout(layout.map(x => {
      if (x.key !== key) return x;
      const i = def.widths.indexOf(x.width);
      return { ...x, width: def.widths[(i + 1) % def.widths.length] };
    }));
  };

  const hide = (key: string) => setLayout(layout.map(x => x.key === key ? { ...x, visible: false } : x));

  if (visible.length === 0) {
    // Dwa różne powody pustego pulpitu wymagają różnych komunikatów. Odsyłanie do „Dostosuj"
    // kogoś, kto nie ma uprawnień do ŻADNEGO widgetu, byłoby ślepą uliczką — lista ukrytych
    // też jest wtedy pusta, więc user klikałby w kółko.
    const brakUprawnien = !layout.some(it => widgetAllowedForPerms(it.key, perms));
    return (
      <div className="card"><div className="card-body text-center text-ink-400 py-10">
        {brakUprawnien
          ? 'Twoje uprawnienia nie obejmują żadnego z widgetów pulpitu. Skorzystaj z menu po lewej — masz tam moduły, do których masz dostęp.'
          : <>Twój dashboard jest pusty. {editing ? 'Przywróć widgety z listy „Ukryte" powyżej.' : 'Kliknij „Dostosuj", aby dodać widgety.'}</>}
      </div></div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
      {visible.map((it, vi) => {
        const def = WIDGET_MAP[it.key];
        const isOver = editing && overKey === it.key && dragKey !== it.key;
        return (
          <div
            key={it.key}
            className={'relative h-full rounded-lg ' + spanClass(it.width)
              + (editing ? ' ring-1 ring-ink-200 cursor-move' : '')
              + (isOver ? ' ring-2 ring-water-400' : '')
              + (dragKey === it.key ? ' opacity-50' : '')}
            draggable={editing}
            onDragStart={editing ? (e => {
              setDragKey(it.key);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', it.key);
            }) : undefined}
            onDragOver={editing ? (e => { e.preventDefault(); if (overKey !== it.key) setOverKey(it.key); }) : undefined}
            onDrop={editing ? (e => { e.preventDefault(); reorder(dragKey, it.key); setDragKey(null); setOverKey(null); }) : undefined}
            onDragEnd={editing ? (() => { setDragKey(null); setOverKey(null); }) : undefined}
          >
            {editing && (
              <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1 bg-white/90 border border-ink-200 rounded-md shadow-sm px-1 py-0.5">
                <span className="text-ink-300 px-0.5" title="Przeciągnij, aby zmienić kolejność">
                  <Icon name="grip" className="w-3.5 h-3.5"/>
                </span>
                <button type="button" onClick={() => step(it.key, -1)} disabled={vi === 0}
                        className="text-ink-400 hover:text-water-600 disabled:opacity-30 disabled:hover:text-ink-400 px-0.5"
                        title="Przesuń wcześniej" aria-label={`Przesuń „${def.title}" wcześniej`}>
                  <Icon name="chevrons-left" className="w-3.5 h-3.5"/>
                </button>
                <button type="button" onClick={() => step(it.key, 1)} disabled={vi === visible.length - 1}
                        className="text-ink-400 hover:text-water-600 disabled:opacity-30 disabled:hover:text-ink-400 px-0.5"
                        title="Przesuń dalej" aria-label={`Przesuń „${def.title}" dalej`}>
                  <Icon name="chevrons-right" className="w-3.5 h-3.5"/>
                </button>
                {def.widths.length > 1 && (
                  <button type="button" onClick={() => cycleWidth(it.key)}
                          className="text-ink-500 hover:text-water-600 px-1 text-[11px] font-mono font-semibold"
                          title="Zmień szerokość">
                    {WIDTH_LABEL[it.width]}
                  </button>
                )}
                <button type="button" onClick={() => hide(it.key)}
                        className="text-ink-400 hover:text-red-500 px-0.5" title="Ukryj widget">
                  <Icon name="x" className="w-3.5 h-3.5"/>
                </button>
              </div>
            )}
            <div className={'h-full' + (editing ? ' pointer-events-none select-none' : '')}>
              {def.render(data)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
